import { getAssessmentScoreCategory } from "@repo/app-types/constants/assessmentScore";
import type { Measurement } from "@repo/app-types/entities/interview";
import { MeasurementScale } from "@repo/app-types/entities/interview";
import { Size } from "@repo/ui/helpers/helpers";
import { getPillColor, Pill, type PillColor } from "@repo/ui/Pills/pill";
import type { FC, ReactNode } from "react";
import { SizedContainer } from "../../components/sizedContainer.js";

interface ScoreProps {
  color: PillColor;
  text: string;
}

interface AssessmentScoreProps {
  score: number | Measurement;
  size?: Size;
  onClick?: () => void;
  /**
   * Sizes the pill to the top of the score's scale rather than to the score it
   * does show, so that a column of them lines up.
   */
  sizedToMax?: boolean;
  /**
   * Sizes the pill to this content instead, for a column that holds more than
   * scores. Takes precedence over `sizedToMax`.
   */
  childrenForSize?: ReactNode;
}

interface LabeledAssessmentScoreProps {
  score: number | Measurement;
  size?: Size;
  onClick?: () => void;
}

/** Top of the 1-5 scale — the widest an `AssessmentScore` can render. */
const maxScore = 5;

/** The score pill on its own, e.g. `4`. */
export const AssessmentScore: FC<AssessmentScoreProps> = ({
  score,
  size = Size.SMALL,
  onClick,
  sizedToMax,
  childrenForSize,
}) => {
  const scoreProps = getScoreProps(score);

  const maxLabel = renderScore(
    typeof score === "object" ? { ...score, value: maxScore } : maxScore,
  );

  return (
    <div className="flex max-w-max items-center gap-2">
      <Pill
        color={scoreProps.color}
        label={
          // `tabular-nums`, so that every digit is the width the sizer
          // measured — Inter's proportional `4` is wider than its `5`.
          <SizedContainer
            className="text-center tabular-nums"
            childrenForSize={childrenForSize ?? (sizedToMax ? maxLabel : null)}
          >
            {renderScore(score)}
          </SizedContainer>
        }
        size={size}
        onClick={onClick}
      />
    </div>
  );
};

/** The score pill with its category alongside, e.g. `4 · Good`. */
export const LabeledAssessmentScore: FC<LabeledAssessmentScoreProps> = ({
  score,
  size = Size.SMALL,
  onClick,
}) => {
  const scoreProps = getScoreProps(score);

  return (
    <div className="flex max-w-max items-center gap-2">
      <Pill
        color={scoreProps.color}
        label={
          <span className="truncate">
            {renderScore(score)} · {scoreProps.text}
          </span>
        }
        size={size}
        onClick={onClick}
      />
    </div>
  );
};

export function getScoreProps(
  scoreOrMeasurement: number | Measurement,
): ScoreProps {
  const score =
    typeof scoreOrMeasurement === "object"
      ? scoreOrMeasurement.value
      : scoreOrMeasurement;
  const { color, label } = getAssessmentScoreCategory(score);

  return {
    color: getPillColor(color),
    text: label,
  };
}

export function renderScore(score: number | Measurement) {
  const { scale, value } =
    typeof score === "object"
      ? score
      : { scale: MeasurementScale.FivePointOrdinal, value: score };

  switch (scale) {
    case MeasurementScale.FivePointInterval:
      // Show one 1 decimal point.
      return value.toFixed(1);

    default:
      return Math.round(value);
  }
}
